/**
 * Шрифты сцены.
 *
 * ГЛАВНОЕ ЗДЕСЬ — ШРИФТ ПРИМЕНЯЕТСЯ ТОЛЬКО ПОСЛЕ `document.fonts.ready`.
 *
 * Иначе браузер рисует надпись запасным шрифтом, а через долю секунды
 * перерисовывает нужным — с другой шириной букв. В обычной вкладке
 * это «мелькнуло», в эфире это ПРЫЖОК ТЕКСТА, который увидят все
 * зрители и который никто из них не спишет на загрузку шрифта.
 * Хуже того, оверлей стоит поверх игры: дёрнувшийся заголовок
 * выглядит поломкой трансляции.
 *
 * Ждать при этом обязаны МЫ, а не браузер: `font-display` управляет
 * тем, ЧЕМ рисовать во время загрузки, но не тем, рисовать ли вообще.
 *
 * ВТОРОЕ: браузер без Font Loading API считается готовым сразу.
 * Старые сборки CEF в OBS встречаются, и текст, который в них
 * не появится никогда, — гораздо хуже текста запасным шрифтом.
 */
/**
 * Запасные шрифты.
 *
 * Стоят в конце ЛЮБОГО списка, даже когда свой шрифт готов: файл
 * мог не скачаться, и надпись без запасного не отрисуется вовсе.
 */
export const FALLBACK_FAMILY = "system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";
/**
 * Состояние модуля.
 *
 * Модульное по той же причине, что звуковой граф: готовность
 * шрифтов — свойство СТРАНИЦЫ, а не отдельного слоя, и спрашивать
 * её у каждого виджета через контракт значило бы объяснять шрифты
 * каждому следующему примитиву.
 */
let ready = false;
let watching = false;
const registered = new Map();
const waiting = new Set();
/**
 * Где живут шрифты страницы.
 *
 * Запоминается при `watchFonts`, чтобы виджету не приходилось его
 * знать: слой просит шрифт, а на какой странице он окажется —
 * не его забота. Тесту же подменять надо один раз, а не в каждом
 * вызове.
 */
let page = null;
/** Готовы ли шрифты страницы. Спрашивается синхронно, на отрисовке. */
export function fontsReady() {
    return ready;
}
/**
 * Подписка на готовность.
 *
 * Уже готовым отвечает СРАЗУ и синхронно: слой, смонтированный
 * после загрузки шрифтов, не должен ждать следующего события —
 * его не будет.
 */
export function whenFontsReady(listener) {
    if (ready) {
        listener();
        return () => { };
    }
    waiting.add(listener);
    return () => waiting.delete(listener);
}
/**
 * Начинает ждать готовности шрифтов страницы.
 *
 * Зовётся один раз при старте оверлея. Повторные вызовы ничего
 * не стоят: подписка на `document.fonts.ready` заводится однажды.
 */
export function watchFonts(host = hostDocument()) {
    if (watching)
        return;
    watching = true;
    page = host;
    const fonts = host.fonts;
    if (fonts === undefined) {
        // Браузер без Font Loading API. Считаем готовым немедленно:
        // текст, который не появится никогда, хуже текста запасным
        // шрифтом
        markReady();
        return;
    }
    void fonts.ready.then(markReady, markReady);
}
/**
 * Заводит шрифт на странице.
 *
 * Повторная регистрация того же семейства с тем же адресом ничего
 * не делает: слоёв с одним шрифтом на сцене бывает десяток,
 * и качать файл на каждый значило бы десять запросов за одним
 * и тем же.
 *
 * ОТКАЗ НЕ РОНЯЕТ НИЧЕГО. Не скачался шрифт — надпись выйдет
 * запасным, и это единственный разумный исход: гасить слой из-за
 * шрифта значит убрать со сцены то, ради чего её собирали.
 */
export function registerFont(spec, host = page ?? hostDocument()) {
    if (spec.family === '' || spec.src === '')
        return;
    if (registered.get(spec.family) === spec.src)
        return;
    registered.set(spec.family, spec.src);
    const fonts = host.fonts;
    if (fonts === undefined)
        return;
    // FontFace есть не везде, где есть document.fonts: проверяем
    // отдельно, потому что бросок отсюда уронил бы отрисовку сцены
    const Face = globalThis.FontFace;
    if (Face === undefined)
        return;
    try {
        const face = new Face(spec.family, `url(${spec.src})`);
        fonts.add?.(face);
        // Загрузка запускается явно: добавленный, но не загруженный
        // FontFace остаётся в состоянии `unloaded`, и `fonts.ready`
        // о нём даже не вспомнит — то есть ждать будет нечего
        void face.load().catch(() => { });
    }
    catch {
        // Негодный адрес или отказ браузера. Слой выйдет запасным
        // шрифтом — см. выше
    }
}
/**
 * Собирает значение `font-family` для слоя.
 *
 * ДО ГОТОВНОСТИ ВОЗВРАЩАЕТ ТОЛЬКО ЗАПАСНЫЕ — в этом вся суть
 * модуля: пока шрифт не готов, его имени в стиле быть не должно,
 * иначе браузер отрисует запасным и перерисует своим.
 */
export function familyFor(family) {
    if (family === '' || !ready)
        return FALLBACK_FAMILY;
    // Кавычки обязательны: имена вроде `Comic Sans MS` без них
    // разбираются как список из трёх семейств
    return `'${family.replaceAll("'", '')}', ${FALLBACK_FAMILY}`;
}
/** Сбрасывает состояние. Нужен тестам: страница у них одна на все. */
export function resetFonts() {
    ready = false;
    watching = false;
    page = null;
    registered.clear();
    waiting.clear();
}
function hostDocument() {
    return document;
}
function markReady() {
    // Зовётся ровно один раз: `watching` не даёт завести второе
    // ожидание, а обещание срабатывает единожды
    ready = true;
    // Копия списка: подписчик вправе отписаться прямо в обработчике,
    // а правка множества во время перебора теряет соседей
    for (const listener of [...waiting]) {
        listener();
    }
    waiting.clear();
}
//# sourceMappingURL=fonts.js.map